import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as alertsApi from '@/api/alerts.api';

/**
 * Query keys for alerts
 */
export const alertKeys = {
  all: ['alerts'] as const,
  lists: () => [...alertKeys.all, 'list'] as const,
  list: (params: alertsApi.AlertQueryParams) => [...alertKeys.lists(), params] as const,
  stats: (params?: alertsApi.AlertStatsParams) => [...alertKeys.all, 'stats', params || {}] as const,
};

/**
 * Fetch alerts with filters
 */
export function useAlerts(params?: alertsApi.AlertQueryParams) {
  return useQuery({
    queryKey: alertKeys.list(params || {}),
    queryFn: () => alertsApi.getAlerts(params),
  });
}

/**
 * Fetch alert statistics
 */
export function useAlertStats(params?: alertsApi.AlertStatsParams) {
  return useQuery({
    queryKey: alertKeys.stats(params),
    queryFn: () => alertsApi.getAlertStats(params),
  });
}

/**
 * Acknowledge alert mutation
 */
export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => alertsApi.acknowledgeAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertKeys.all });
    },
  });
}

/**
 * Resolve alert mutation
 */
export function useResolveAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => alertsApi.resolveAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertKeys.all });
    },
  });
}

/**
 * Acknowledge multiple alerts mutation
 */
export function useAcknowledgeAlerts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => alertsApi.acknowledgeAlerts(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertKeys.all });
    },
  });
}

/**
 * Acknowledge all unacknowledged alerts mutation
 */
export function useAcknowledgeAllAlerts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => alertsApi.acknowledgeAllAlerts(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertKeys.all });
    },
  });
}
